/**
 * Rozmístění zvuku do reproduktorů 5.1 podle směru (azimutu).
 * Úhly podle ITU-R BS.775: C 0°, L/R ±30°, Ls/Rs ±110°; kladný úhel = vpravo.
 * Mezi dvěma sousedními reproduktory se panuje s konstantním výkonem.
 */

/** Pořadí kanálů ve sběrnici 5.1 (Web Audio „speakers“): L, R, C, LFE, Ls, Rs. */
export const CH = { L: 0, R: 1, C: 2, LFE: 3, Ls: 4, Rs: 5 } as const;

/** Reproduktory dokola po směru hodinových ručiček (úhly 0–360°). */
const RING: { ch: number; deg: number }[] = [
  { ch: CH.C, deg: 0 },
  { ch: CH.R, deg: 30 },
  { ch: CH.Rs, deg: 110 },
  { ch: CH.Ls, deg: 250 },
  { ch: CH.L, deg: 330 },
  { ch: CH.C, deg: 360 },
];

/**
 * Zesílení pro 6 kanálů (LFE = 0, řeší se zvlášť).
 * `spread` 0–1 rozprostře zvuk do všech reproduktorů (blízký nebo nesměrový zdroj).
 */
export function surroundGains(azimuthDeg: number, spread = 0): number[] {
  const g = [0, 0, 0, 0, 0, 0];
  const az = ((azimuthDeg % 360) + 360) % 360;
  for (let i = 0; i < RING.length - 1; i++) {
    const a = RING[i];
    const b = RING[i + 1];
    if (az < a.deg || az > b.deg) continue;
    const t = (az - a.deg) / (b.deg - a.deg);
    g[a.ch] += Math.cos((t * Math.PI) / 2);
    g[b.ch] += Math.sin((t * Math.PI) / 2);
    break;
  }
  if (spread > 0) {
    const even = 1 / Math.sqrt(5);
    for (const ch of [CH.L, CH.R, CH.C, CH.Ls, CH.Rs]) g[ch] = g[ch] * (1 - spread) + even * spread;
    // Zachovat celkový výkon
    const p = Math.sqrt(g.reduce((s, v) => s + v * v, 0)) || 1;
    for (let i = 0; i < 6; i++) g[i] /= p;
  }
  return g;
}

/** Do této vzdálenosti (px ve světě) zní zdroj naplno. */
const NEAR = 150;
/** Nejtišší útlum (−18 dB) – i výbuch na druhém konci mapy je slyšet. */
const MIN_GAIN = 0.12;
/** Vzdálenost, kde je dolní propust nejníž (zhruba šířka mapy). */
const FAR = 1600;
const CUTOFF_NEAR = 20000;
const CUTOFF_FAR = 2500;

/** Útlum podle vzdálenosti od posluchače: do NEAR plně, dál 1/d, nejméně MIN_GAIN. */
export function distanceGain(d: number): number {
  return Math.max(MIN_GAIN, Math.min(1, NEAR / Math.max(NEAR, d)));
}

/** Dolní propust podle vzdálenosti: vzdálené zvuky zní tlumeněji (logaritmicky NEAR → FAR). */
export function distanceCutoff(d: number): number {
  const t = Math.max(0, Math.min(1, (d - NEAR) / (FAR - NEAR)));
  return CUTOFF_NEAR * Math.pow(CUTOFF_FAR / CUTOFF_NEAR, t);
}

/**
 * Azimut zdroje ve 2D pohledu: kamera se dívá na bitvu zepředu. Zdroje na
 * obrazovce jsou vpředu (L–C–R do ±35°), daleko mimo obraz se stáčí do stran
 * a dozadu (až ±115°).
 */
export function azimuth2D(dx: number, halfSpan: number): number {
  const k = dx / Math.max(1, halfSpan);
  const a = Math.abs(k);
  const deg = a <= 1 ? a * 35 : Math.min(115, 35 + (a - 1) * 40);
  return Math.sign(k) * deg;
}

/**
 * Azimut zdroje ve 3D: úhel mezi směrem pohledu (v rovině xz) a směrem ke zdroji.
 * `forward` je směr kamery, kladný výsledek = vpravo.
 */
export function azimuth3D(listener: { x: number; z: number }, forward: { x: number; z: number }, src: { x: number; z: number }): number {
  const fl = Math.hypot(forward.x, forward.z) || 1;
  const fx = forward.x / fl;
  const fz = forward.z / fl;
  const dx = src.x - listener.x;
  const dz = src.z - listener.z;
  // Vpravo od směru pohledu (Three.js: y nahoru) = (−fz, fx)
  const right = dx * -fz + dz * fx;
  const front = dx * fx + dz * fz;
  if (Math.abs(right) < 1e-6 && Math.abs(front) < 1e-6) return 0;
  return (Math.atan2(right, front) * 180) / Math.PI;
}
